import { PRICE_GBP } from '@bandcheck/server/modules/pricing'
import { StyleSheet, Text, View } from 'react-native'
import { colors, fontFamily, fontSize, space } from '../tokens'
import { EvidenceBlockHeadline } from './EvidenceBlockHeadline'
import { TeaserComparable } from './TeaserComparable'

type EvidenceBlock = {
  id: string
  name: string
  suggestedBand: string | null
  reasoning?: readonly string[]
}

type Comparable = {
  address: string
  band: string
  lastSalePrice: number | null
  lastSaleDate: string | null
}

type Props = {
  includedBlocks: readonly EvidenceBlock[]
  otherBlocks: readonly EvidenceBlock[]
  teaserComparable: Comparable | undefined
  totalComparableCount: number
  showTeaser: boolean
  // View-only jurisdictions (Scotland for now) get the included-block
  // reasoning inline because there's no £4.99 pack to defer it into. E&W
  // keeps the lockNote since the full paragraph reasoning ships in the
  // paid PDF.
  inlineReasoning: boolean
}

export const BandCheckResultEvidence = ({
  includedBlocks,
  otherBlocks,
  teaserComparable,
  totalComparableCount,
  showTeaser,
  inlineReasoning,
}: Props) => (
  <View style={styles.wrap}>
    {includedBlocks.length > 0 ? (
      <>
        <Text style={styles.sectionTitle}>What we found</Text>
        {includedBlocks.map((b) => (
          <View key={b.id} style={styles.includedRow}>
            <EvidenceBlockHeadline
              name={b.name}
              suggestedBand={b.suggestedBand}
              variant="included"
            />
            {inlineReasoning && b.reasoning && b.reasoning.length > 0 ? (
              <View style={styles.reasoningGroup}>
                {b.reasoning.map((line, idx) => (
                  <Text key={`${b.id}-r-${idx}`} style={styles.reasoningLine}>
                    {line}
                  </Text>
                ))}
              </View>
            ) : null}
          </View>
        ))}
        {!inlineReasoning ? (
          <Text style={styles.lockNote}>
            Full paragraph reasoning is included in your {PRICE_GBP} pack.
          </Text>
        ) : null}
      </>
    ) : null}

    {showTeaser && teaserComparable ? (
      <TeaserComparable comparable={teaserComparable} totalCount={totalComparableCount} />
    ) : null}

    {otherBlocks.length > 0 ? (
      <>
        <Text style={styles.sectionTitle}>{`We also checked (${otherBlocks.length})`}</Text>
        {otherBlocks.map((b) => (
          <EvidenceBlockHeadline
            key={b.id}
            name={b.name}
            suggestedBand={b.suggestedBand}
            variant="checked"
          />
        ))}
      </>
    ) : null}
  </View>
)

const styles = StyleSheet.create({
  wrap: { marginTop: space[24] },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: colors.muted,
    marginTop: space[16],
    marginBottom: space[8],
    fontFamily: fontFamily.body,
  },
  lockNote: {
    fontSize: fontSize.tiny,
    color: colors.faint,
    fontStyle: 'italic',
    marginTop: space[8],
    fontFamily: fontFamily.body,
  },
  includedRow: {
    marginBottom: space[8],
  },
  reasoningGroup: {
    marginTop: space[4],
    paddingLeft: space[16],
    gap: space[4],
  },
  reasoningLine: {
    fontSize: 13,
    lineHeight: 19,
    color: colors.ink,
    fontFamily: fontFamily.body,
  },
})
